import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDataProvider, useNotify, Title, useTranslate } from 'react-admin';
import { 
  Box, 
  Button, 
  Paper, 
  Typography, 
  Chip, 
  CircularProgress, 
  useTheme,
  alpha,
  Theme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  IconButton,
  TextField,
  Autocomplete,
  Switch,
  FormControlLabel,
  Alert,
} from '@mui/material';
import { 
  Delete, 
  Refresh, 
  CheckCircle, 
  Business, 
  Flag, 
  ArrowForward,
  Info,
  Add,
  Warning,
  Block,
} from '@mui/icons-material';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import {
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';

interface StatusNode {
  id: number;
  name: string;
  order: number;
  triggersMilestone?: string | null;
  triggersOutcome?: string | null;
  isPostHire: boolean;
  // Structural flags resolved from the pipeline-health endpoint. These are the
  // real drivers of the workflow (the legacy triggersOutcome is deprecated).
  marksOutcome?: 'hired' | null;
  isTerminal?: boolean;
  requiresClosureReason?: boolean;
  hidesFromKanban?: boolean;
}

interface PipelineHealthStatus {
  id: number;
  name: string;
  order: number;
  marksOutcome?: 'hired' | null;
  isTerminal: boolean;
  requiresClosureReason: boolean;
  isPostHire: boolean;
  hidesFromKanban: boolean;
}

interface PipelineHealthData {
  hireStatusId: number | null;
  hireStatusName: string | null;
  terminationStatusId: number | null;
  terminationStatusName: string | null;
  statuses: PipelineHealthStatus[];
  warnings: string[];
}

/**
 * Visual classification of a status, driven by the structural flags
 * (marksOutcome / isTerminal) rather than the deprecated triggersOutcome.
 */
type StatusKind = 'hire' | 'terminal' | 'postHire' | 'pipeline';

const classifyStatus = (status: StatusNode): StatusKind => {
  if (status.marksOutcome === 'hired') return 'hire';
  if (status.isTerminal) return 'terminal';
  if (status.isPostHire) return 'postHire';
  return 'pipeline';
};

interface Transition {
  id: number;
  fromStatusId: number;
  toStatusId: number;
  isActive: boolean;
  order?: number | null;
}

interface WorkflowGraph {
  statuses: StatusNode[];
  transitions: Transition[];
}

// Status Edit Modal Component
const StatusEditModal: React.FC<{
  status: StatusNode | null;
  workflow: WorkflowGraph | null;
  open: boolean;
  onClose: () => void;
  onDeleteTransition: (transitionId: number) => Promise<void>;
  onCreateTransition: (fromStatusId: number, toStatusId: number) => Promise<void>;
  translate: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
  saving: boolean;
}> = ({ status, workflow, open, onClose, onDeleteTransition, onCreateTransition, translate, saving }) => {
  const [newTransitionTo, setNewTransitionTo] = useState<StatusNode | null>(null);

  if (!status || !workflow) return null;

  const outgoingTransitions = workflow.transitions.filter(t => 
    t.fromStatusId === status.id && t.isActive
  );
  const incomingTransitions = workflow.transitions.filter(t => 
    t.toStatusId === status.id && t.isActive
  );

  const availableTargets = workflow.statuses.filter(s => 
    s.id !== status.id && 
    !workflow.transitions.some(t => t.fromStatusId === status.id && t.toStatusId === s.id && t.isActive)
  );

  const getStatusName = (statusId: number) => {
    return workflow.statuses.find(s => s.id === statusId)?.name || `ID: ${statusId}`;
  };

  const handleCreateTransition = async () => {
    if (!newTransitionTo) return;
    await onCreateTransition(status.id, newTransitionTo.id);
    setNewTransitionTo(null);
  };

  const getMilestoneLabel = (milestone: string) => {
    const key = `resources.job-application-status.milestoneLabels.${milestone}`;
    return translate(key);
  };

  const getOutcomeLabel = (outcome: string) => {
    const key = `resources.job-application-status.outcomeLabels.${outcome}`;
    return translate(key);
  };

  const kind = classifyStatus(status);
  const statusType =
    kind === 'hire'
      ? { label: translate('resources.job-application-status.workflow.nodeTypesV2.hire'), color: '#f59e0b', icon: Flag }
      : kind === 'terminal'
      ? { label: translate('resources.job-application-status.workflow.nodeTypesV2.terminal'), color: '#ef4444', icon: Block }
      : kind === 'postHire'
      ? { label: translate('resources.job-application-status.workflow.nodeTypesV2.postHire'), color: '#3b82f6', icon: Business }
      : { label: translate('resources.job-application-status.workflow.nodeTypesV2.pipeline'), color: '#10b981', icon: CheckCircle };

  const Icon = statusType.icon;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(statusType.color, 0.1),
              color: statusType.color,
            }}
          >
            <Icon />
          </Box>
          <Box>
            <Typography variant="h6">{status.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {statusType.label}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Status Info */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {translate('resources.job-application-status.workflow.modal.statusInfo')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {status.triggersMilestone && (
                <Chip
                  icon={<Info />}
                  label={getMilestoneLabel(status.triggersMilestone)}
                  size="small"
                />
              )}
              {status.triggersOutcome && (
                <Chip
                  icon={<Flag />}
                  label={getOutcomeLabel(status.triggersOutcome)}
                  size="small"
                  color="error"
                />
              )}
            </Box>
          </Box>

          {/* Outgoing Transitions */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {translate('resources.job-application-status.workflow.modal.outgoingTransitions')} ({outgoingTransitions.length})
            </Typography>
            {outgoingTransitions.length > 0 ? (
              <List dense>
                {outgoingTransitions.map(transition => (
                  <ListItem
                    key={transition.id}
                    secondaryAction={
                      <IconButton
                        edge="end"
                        onClick={() => onDeleteTransition(transition.id)}
                        disabled={saving}
                        color="error"
                        size="small"
                      >
                        <Delete />
                      </IconButton>
                    }
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {status.name}
                          </Typography>
                          <ArrowForward sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {getStatusName(transition.toStatusId)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {translate('resources.job-application-status.workflow.modal.noOutgoingTransitions')}
              </Typography>
            )}
          </Box>

          {/* Incoming Transitions */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              {translate('resources.job-application-status.workflow.modal.incomingTransitions')} ({incomingTransitions.length})
            </Typography>
            {incomingTransitions.length > 0 ? (
              <List dense>
                {incomingTransitions.map(transition => (
                  <ListItem key={transition.id}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {getStatusName(transition.fromStatusId)}
                          </Typography>
                          <ArrowForward sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight={500}>
                            {status.name}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            ) : (
              <Typography variant="body2" color="text.secondary">
                {translate('resources.job-application-status.workflow.modal.noIncomingTransitions')}
              </Typography>
            )}
          </Box>

          {/* Add New Transition */}
          {availableTargets.length > 0 && (
            <Box>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {translate('resources.job-application-status.workflow.modal.addTransition')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <Autocomplete
                  options={availableTargets}
                  getOptionLabel={(option) => option.name}
                  value={newTransitionTo}
                  onChange={(_e, newValue) => setNewTransitionTo(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={translate('resources.job-application-status.workflow.modal.selectTargetStatus')}
                      size="small"
                      fullWidth
                    />
                  )}
                  sx={{ flex: 1 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleCreateTransition}
                  disabled={!newTransitionTo || saving}
                >
                  {translate('resources.job-application-status.workflow.modal.createTransition')}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          {translate('resources.job-application-status.workflow.modal.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Columns View Component - More compact visualization
const ColumnsView: React.FC<{
  workflow: WorkflowGraph | null;
  onDeleteTransition: (transitionId: number) => Promise<void>;
  onCreateTransition: (fromStatusId: number, toStatusId: number) => Promise<void>;
  translate: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
  saving: boolean;
  onStatusClick: (status: StatusNode) => void;
}> = ({ workflow, onCreateTransition, translate, theme, onStatusClick }) => {
  const [draggedFrom, setDraggedFrom] = useState<number | null>(null);
  const [hoveredTo, setHoveredTo] = useState<number | null>(null);

  if (!workflow) return null;

  // Group statuses by the structural flags that actually drive the pipeline
  // (marksOutcome / isTerminal). triggersOutcome is deprecated and no longer
  // used for visual classification.
  const pipelineStatuses = workflow.statuses.filter(s => classifyStatus(s) === 'pipeline');
  const hireStatuses = workflow.statuses.filter(s => classifyStatus(s) === 'hire');
  const postHireStatuses = workflow.statuses.filter(s => classifyStatus(s) === 'postHire');
  const terminalStatuses = workflow.statuses.filter(s => classifyStatus(s) === 'terminal');

  const hasTransition = (fromId: number, toId: number) =>
    workflow.transitions.some(t => t.fromStatusId === fromId && t.toStatusId === toId && t.isActive);

  const handleDragStart = (statusId: number) => {
    setDraggedFrom(statusId);
  };

  const handleDragEnd = () => {
    if (draggedFrom && hoveredTo && draggedFrom !== hoveredTo) {
      if (!hasTransition(draggedFrom, hoveredTo)) {
        onCreateTransition(draggedFrom, hoveredTo);
      }
    }
    setDraggedFrom(null);
    setHoveredTo(null);
  };

  const kindConfig = (kind: StatusKind) =>
    kind === 'hire'
      ? { bg: '#f59e0b', icon: Flag, label: translate('resources.job-application-status.workflow.nodeTypesV2.hire') }
      : kind === 'terminal'
      ? { bg: '#ef4444', icon: Block, label: translate('resources.job-application-status.workflow.nodeTypesV2.terminal') }
      : kind === 'postHire'
      ? { bg: '#3b82f6', icon: Business, label: translate('resources.job-application-status.workflow.nodeTypesV2.postHire') }
      : { bg: '#10b981', icon: CheckCircle, label: translate('resources.job-application-status.workflow.nodeTypesV2.pipeline') };

  const StatusCard: React.FC<{ status: StatusNode; columnType: StatusKind }> = ({ status, columnType }) => {
    const config = kindConfig(columnType);

    const Icon = config.icon;
    const transitions = workflow.transitions.filter(t => 
      (t.fromStatusId === status.id || t.toStatusId === status.id) && t.isActive
    );

    return (
      <Paper
        draggable
        onClick={() => onStatusClick(status)}
        onDragStart={() => handleDragStart(status.id)}
        onDragEnd={handleDragEnd}
        onDragOver={(e) => {
          e.preventDefault();
          if (draggedFrom && draggedFrom !== status.id) {
            setHoveredTo(status.id);
          }
        }}
        onDragLeave={() => setHoveredTo(null)}
        onDrop={(e) => {
          e.preventDefault();
          handleDragEnd();
        }}
        elevation={draggedFrom === status.id ? 8 : hoveredTo === status.id ? 6 : 2}
        sx={{
          p: 2,
          mb: 1.5,
          cursor: 'pointer',
          border: `2px solid ${hoveredTo === status.id ? theme.palette.primary.main : 'transparent'}`,
          bgcolor: hoveredTo === status.id ? alpha(theme.palette.primary.main, 0.1) : 'background.paper',
          transition: 'all 0.2s',
          '&:active': { cursor: 'grabbing' },
          '&:hover': { 
            transform: 'translateY(-2px)',
            boxShadow: theme.shadows[4],
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Icon sx={{ color: config.bg, fontSize: 20 }} />
          <Typography variant="body2" fontWeight={600}>
            {status.name}
          </Typography>
        </Box>
        {status.triggersMilestone && (
          <Chip
            label={translate(`resources.job-application-status.milestoneLabels.${status.triggersMilestone}`)}
            size="small"
            sx={{ mt: 0.5, fontSize: '0.7rem', height: 20 }}
          />
        )}
        {transitions.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {transitions.length} transição(ões)
          </Typography>
        )}
      </Paper>
    );
  };

  const columns: { kind: StatusKind; statuses: StatusNode[] }[] = [
    { kind: 'pipeline', statuses: pipelineStatuses },
    { kind: 'hire', statuses: hireStatuses },
    { kind: 'postHire', statuses: postHireStatuses },
    { kind: 'terminal', statuses: terminalStatuses },
  ];

  return (
    <Box sx={{ flex: 1, display: 'flex', gap: 2, p: 3, overflow: 'auto' }}>
      {columns.map(({ kind, statuses }) => {
        const config = kindConfig(kind);
        const ColumnIcon = config.icon;
        return (
          <Box key={kind} sx={{ flex: 1, minWidth: 240 }}>
            <Paper sx={{ p: 2, mb: 2, bgcolor: alpha(config.bg, 0.1), border: `2px solid ${config.bg}` }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <ColumnIcon sx={{ color: config.bg }} />
                <Typography variant="h6" fontWeight={600}>
                  {config.label}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {statuses.length} status
              </Typography>
            </Paper>
            <Box>
              {statuses.map(status => (
                <StatusCard key={status.id} status={status} columnType={kind} />
              ))}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

// Read-only Pipeline Health view. Surfaces the resolved hire / termination
// statuses and the structural warnings produced by the backend, plus a table of
// every status with its driving flags.
const PipelineHealthView: React.FC<{
  health: PipelineHealthData | null;
  loading: boolean;
  translate: (key: string, options?: Record<string, unknown>) => string;
  theme: Theme;
}> = ({ health, loading, translate, theme }) => {
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!health) {
    return (
      <Alert severity="error" sx={{ m: 3 }}>
        {translate('resources.job-application-status.workflow.pipelineHealth.loadingError')}
      </Alert>
    );
  }

  const t = (key: string) =>
    translate(`resources.job-application-status.workflow.pipelineHealth.${key}`);
  const notConfigured = t('notConfigured');

  const flagChips = (status: PipelineHealthStatus) => {
    const chips: { key: string; color: 'warning' | 'error' | 'info' | 'default' | 'primary' }[] = [];
    if (status.marksOutcome === 'hired') chips.push({ key: 'hire', color: 'warning' });
    if (status.isTerminal) chips.push({ key: 'terminal', color: 'error' });
    if (status.requiresClosureReason) chips.push({ key: 'requiresClosureReason', color: 'primary' });
    if (status.isPostHire) chips.push({ key: 'postHire', color: 'info' });
    if (status.hidesFromKanban) chips.push({ key: 'hiddenFromKanban', color: 'default' });
    return chips;
  };

  return (
    <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
      <Stack spacing={3}>
        {/* Resolved roles */}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Paper sx={{ p: 2, flex: 1, minWidth: 240, bgcolor: alpha('#f59e0b', 0.1), border: `2px solid #f59e0b` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Flag sx={{ color: '#f59e0b' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('hireStatus')}
              </Typography>
            </Box>
            <Typography variant="body1">
              {health.hireStatusName ?? notConfigured}
            </Typography>
          </Paper>
          <Paper sx={{ p: 2, flex: 1, minWidth: 240, bgcolor: alpha('#ef4444', 0.1), border: `2px solid #ef4444` }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Block sx={{ color: '#ef4444' }} />
              <Typography variant="subtitle1" fontWeight={600}>
                {t('terminationStatus')}
              </Typography>
            </Box>
            <Typography variant="body1">
              {health.terminationStatusName ?? notConfigured}
            </Typography>
          </Paper>
        </Box>

        {/* Warnings */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Warning sx={{ color: theme.palette.warning.main }} />
            <Typography variant="subtitle1" fontWeight={600}>
              {t('warningsTitle')} ({health.warnings.length})
            </Typography>
          </Box>
          {health.warnings.length > 0 ? (
            <Stack spacing={1}>
              {health.warnings.map((warning, idx) => (
                <Alert key={idx} severity="warning">
                  {warning}
                </Alert>
              ))}
            </Stack>
          ) : (
            <Alert severity="success">{t('noWarnings')}</Alert>
          )}
        </Box>

        {/* Statuses table */}
        <Box>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
            {t('statusesTitle')}
          </Typography>
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={80}>{t('columns.order')}</TableCell>
                  <TableCell>{t('columns.name')}</TableCell>
                  <TableCell>{t('columns.flags')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...health.statuses]
                  .sort((a, b) => a.order - b.order)
                  .map((status) => (
                    <TableRow key={status.id}>
                      <TableCell>{status.order}</TableCell>
                      <TableCell>{status.name}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {flagChips(status).map((chip) => (
                            <Chip
                              key={chip.key}
                              label={t(`flags.${chip.key}`)}
                              size="small"
                              color={chip.color}
                              variant={chip.color === 'default' ? 'outlined' : 'filled'}
                            />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Stack>
    </Box>
  );
};

export const WorkflowBuilder: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();
  const theme = useTheme();
  const [workflow, setWorkflow] = useState<WorkflowGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<StatusNode | null>(null);
  const [enforceValidation, setEnforceValidation] = useState(true);
  const [loadingConfig, setLoadingConfig] = useState(true);
  const [health, setHealth] = useState<PipelineHealthData | null>(null);
  const [loadingHealth, setLoadingHealth] = useState(true);
  const [activeTab, setActiveTab] = useState<'builder' | 'health'>('builder');

  // Load pipeline health (resolved hire/termination + structural flags + warnings).
  const loadHealth = useCallback(async () => {
    try {
      setLoadingHealth(true);
      const { httpClient, API_URL } = await import('../../httpClient');
      const response = await httpClient(`${API_URL}/job-application-status/config/pipeline-health`, {
        method: 'GET',
      });
      const result = response as { json: { data: PipelineHealthData } };
      setHealth(result.json.data);
    } catch (error) {
      console.error('Error loading pipeline health:', error);
      setHealth(null);
    } finally {
      setLoadingHealth(false);
    }
  }, []);

  // Load transition validation config
  const loadConfig = useCallback(async () => {
    try {
      setLoadingConfig(true);
      const { httpClient, API_URL } = await import('../../httpClient');
      const response = await httpClient(`${API_URL}/job-application-status/config/transition-validation`, {
        method: 'GET',
      });
      const result = response as { json: { data: { enforceStatusTransitionValidation: boolean } } };
      setEnforceValidation(result.json.data.enforceStatusTransitionValidation);
    } catch (error) {
      console.error('Error loading config:', error);
      // Default to true if can't load
      setEnforceValidation(true);
    } finally {
      setLoadingConfig(false);
    }
  }, []);

  // Update transition validation config
  const updateConfig = useCallback(async (value: boolean) => {
    try {
      setSaving(true);
      const { httpClient, API_URL } = await import('../../httpClient');
      await httpClient(`${API_URL}/job-application-status/config/transition-validation`, {
        method: 'PATCH',
        body: JSON.stringify({ enforceStatusTransitionValidation: value }),
      });
      setEnforceValidation(value);
      notify(translate('resources.job-application-status.workflow.configUpdateSuccess'), { type: 'success' });
    } catch (error) {
      console.error('Error updating config:', error);
      notify(translate('resources.job-application-status.workflow.configUpdateError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  }, [notify, translate]);

  // Load workflow graph
  const loadWorkflow = useCallback(async () => {
    try {
      setLoading(true);
      // Use httpClient directly for custom endpoint
      const { httpClient, API_URL } = await import('../../httpClient');
      
      const response = await httpClient(`${API_URL}/job-application-status-transition/workflow-graph`, {
        method: 'GET',
      });
      
      // httpClient returns { json: { data: ... } }
      const result = response as { json: { data: WorkflowGraph } };
      setWorkflow(result.json.data);
    } catch (error) {
      console.error('Error loading workflow:', error);
      notify(translate('resources.job-application-status.workflow.loadingError'), { type: 'error' });
    } finally {
      setLoading(false);
    }
  }, [notify, translate]);

  useEffect(() => {
    loadWorkflow();
    loadConfig();
    loadHealth();
  }, [loadWorkflow, loadConfig, loadHealth]);

  // Merge the structural flags (marksOutcome / isTerminal / requiresClosureReason
  // / hidesFromKanban) from the pipeline-health endpoint into the workflow graph
  // nodes, so the builder columns can classify by the real drivers. The
  // workflow-graph endpoint does not expose these flags.
  const mergedWorkflow = useMemo<WorkflowGraph | null>(() => {
    if (!workflow) return null;
    if (!health) return workflow;
    const flagsById = new Map(health.statuses.map((s) => [s.id, s]));
    return {
      ...workflow,
      statuses: workflow.statuses.map((s) => {
        const flags = flagsById.get(s.id);
        return flags
          ? {
              ...s,
              marksOutcome: flags.marksOutcome ?? null,
              isTerminal: flags.isTerminal,
              requiresClosureReason: flags.requiresClosureReason,
              isPostHire: flags.isPostHire,
              hidesFromKanban: flags.hidesFromKanban,
            }
          : s;
      }),
    };
  }, [workflow, health]);

  // Handle transition deletion
  const handleDeleteTransition = async (transitionId: number) => {
    try {
      setSaving(true);
      await dataProvider.delete('job-application-status-transition', {
        id: transitionId,
      });
      notify(translate('resources.job-application-status.workflow.deleteSuccess'), { type: 'success' });
      await loadWorkflow();
      // Refresh modal if open
      if (selectedStatus) {
        setSelectedStatus(mergedWorkflow?.statuses.find(s => s.id === selectedStatus.id) || null);
      }
    } catch (error) {
      console.error('Error deleting transition:', error);
      notify(translate('resources.job-application-status.workflow.deleteError'), { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // Handle transition creation
  const handleCreateTransition = async (fromStatusId: number, toStatusId: number) => {
    // Prevent self-loops
    if (fromStatusId === toStatusId) {
      notify(translate('resources.job-application-status.workflow.selfLoopWarning'), { type: 'warning' });
      return;
    }

    // Check if transition already exists
    const edgeExists = workflow?.transitions.some(
      (t) => t.fromStatusId === fromStatusId && t.toStatusId === toStatusId && t.isActive
    );

    if (edgeExists) {
      notify(translate('resources.job-application-status.workflow.duplicateTransition'), { type: 'warning' });
      return;
    }

    try {
      setSaving(true);
      await dataProvider.create('job-application-status-transition', {
        data: {
          fromStatusId,
          toStatusId,
          isActive: true,
        },
      });
      notify(translate('resources.job-application-status.workflow.createSuccess'), { type: 'success' });
      await loadWorkflow();
      // Refresh modal if open
      if (selectedStatus) {
        setSelectedStatus(mergedWorkflow?.statuses.find(s => s.id === selectedStatus.id) || null);
      }
    } catch (error) {
      console.error('Error creating transition:', error);
      const errorMessage = (error as { body?: { message?: string }; message?: string })?.body?.message || 
                         (error as { message?: string })?.message || 
                         translate('resources.job-application-status.workflow.createError');
      notify(errorMessage, { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)', width: '100%', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Title title={translate('resources.job-application-status.workflow.title')} />
      
      {/* Header */}
      <Box 
        sx={{ 
          p: 2.5, 
          borderBottom: 1, 
          borderColor: 'divider', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          bgcolor: 'background.paper',
          boxShadow: theme.shadows[1],
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              color: theme.palette.primary.main,
            }}
          >
            <AccountTreeIcon />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={600}>
              {translate('resources.job-application-status.workflow.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('resources.job-application-status.workflow.subtitle')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControlLabel
            control={
              <Switch
                checked={enforceValidation}
                onChange={(e) => updateConfig(e.target.checked)}
                disabled={saving || loadingConfig}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                {translate('resources.job-application-status.workflow.enforceValidationLabel')}
              </Typography>
            }
          />
          <Button
            variant="outlined"
            size="medium"
            startIcon={<Refresh />}
            onClick={() => {
              loadWorkflow();
              loadHealth();
            }}
            disabled={loading || saving}
          >
            {translate('resources.job-application-status.workflow.refreshButton')}
          </Button>
        </Box>
      </Box>

      {/* Tabs: Builder vs Pipeline Health */}
      <Tabs
        value={activeTab}
        onChange={(_e, value) => setActiveTab(value)}
        sx={{ px: 2, borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
      >
        <Tab
          value="builder"
          icon={<AccountTreeIcon />}
          iconPosition="start"
          label={translate('resources.job-application-status.workflow.pipelineHealth.tabBuilder')}
        />
        <Tab
          value="health"
          icon={<HealthAndSafetyIcon />}
          iconPosition="start"
          label={translate('resources.job-application-status.workflow.pipelineHealth.tabHealth')}
        />
      </Tabs>

      {/* Validation disabled warning */}
      {activeTab === 'builder' && !enforceValidation && !loadingConfig && (
        <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
          {translate('resources.job-application-status.workflow.validationDisabledWarning')}
        </Alert>
      )}

      {/* Main Content Area */}
      {activeTab === 'builder' ? (
        <ColumnsView
          workflow={mergedWorkflow}
          onDeleteTransition={handleDeleteTransition}
          onCreateTransition={handleCreateTransition}
          translate={translate}
          theme={theme}
          saving={saving}
          onStatusClick={(status) => setSelectedStatus(status)}
        />
      ) : (
        <PipelineHealthView
          health={health}
          loading={loadingHealth}
          translate={translate}
          theme={theme}
        />
      )}

      {/* Status Edit Modal */}
      <StatusEditModal
        status={selectedStatus}
        workflow={mergedWorkflow}
        open={selectedStatus !== null}
        onClose={() => setSelectedStatus(null)}
        onDeleteTransition={handleDeleteTransition}
        onCreateTransition={handleCreateTransition}
        translate={translate}
        theme={theme}
        saving={saving}
      />
    </Box>
  );
};

